"use client";

import { useMemo } from "react";

import { SidebarTopResultsWidgetRow } from "@/components/app/app-layout/sidebar-top-resutls-widget/sidebar-top-results-widget-row";
import type { CreatorStat, TokenMetadatum } from "@/graphql/codegen/graphql";
import { formatUSDCents } from "@/utils/currency";
import { isCreatorStatType } from "@/utils/types";

import { TimePeriod } from "../../discover-sort-constants";
import { WidgetPreset } from "./sidebar-top-results-widget-time-period";

export const DiscoverWidgetUserRow = ({
  user,
  selectedTimePeriod,
  preset,
  baseRedirectUrl,
}: {
  user: CreatorStat | TokenMetadatum;
  preset?: WidgetPreset;
  selectedTimePeriod?: TimePeriod;
  baseRedirectUrl?: string;
}) => {
  const {
    publicKeyBase58Check,
    username,
    displayName,
    totalAssetsBalanceUsdCents,
    totalFocusEarningsUsdCentsAllTime,
    coinCategory,
  } = user;
  const extraData = {
    DisplayName: displayName,
  };

  const earningsToShow = useMemo(() => {
    if (!isCreatorStatType(user)) {
      return user.totalFocusEarningsUsdCentsAllTime;
    }

    const {
      totalFocusEarningsUsdCentsPast24H,
      totalFocusEarningsUsdCentsPast7D,
      totalFocusEarningsUsdCentsPast30D,
    } = user;

    switch (selectedTimePeriod) {
      case TimePeriod.Past24H:
        return totalFocusEarningsUsdCentsPast24H;
      case TimePeriod.Past7D:
        return totalFocusEarningsUsdCentsPast7D;
      case TimePeriod.Past30D:
        return totalFocusEarningsUsdCentsPast30D;
      default:
        return totalFocusEarningsUsdCentsAllTime;
    }
  }, [selectedTimePeriod, totalFocusEarningsUsdCentsAllTime, user]);

  const value =
    preset === WidgetPreset.topEarners
      ? `+${formatUSDCents(earningsToShow ?? 0)}`
      : formatUSDCents(Number(totalAssetsBalanceUsdCents ?? "0"));

  const label = preset === WidgetPreset.topEarners ? "Earned" : "Assets";

  return (
    <SidebarTopResultsWidgetRow
      username={username}
      publicKey={publicKeyBase58Check ?? ""}
      extraData={extraData}
      isCoin={coinCategory !== ""}
      baseRedirectUrl={baseRedirectUrl}
    >
      <>
        <p className="font-shadow-green font-mono text-xs text-emerald-400">
          {value}
        </p>
        <p className="text-xs text-muted">{label}</p>
      </>
    </SidebarTopResultsWidgetRow>
  );
};
